import React, { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger } from "../misc/Tabs";
import PromotionTab from "./PromotionTab";
import { PromotionTabsComponentProps, PromotionType } from "./types";
import TabButtons from "./TabButtons";
import PromotionAssetsTab from "./PromotionAssetsTab";
import { Assets } from "../../types/asset-type";
import { isPromotionType } from "./util";
import { useIsMobile } from "../../hooks/useIsMobile";
import { Button } from "../misc/Button";
import { FaEdit } from "react-icons/fa";
import { getAuthHeader } from "../../utils/authHeader";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import axios from "axios";
import UploadNavigationWarning from "../misc/UploadNavigationWarning";

const TabsComponent: React.FC<PromotionTabsComponentProps> = ({
    isEditing,
    setIsEditing,
    promotion,
    assets,
    activeTab,
    setActiveTab,
    activePromotionType,
    setActivePromotionType,
    carouselApi,
    carouselAssetIndexMap,
    setLoading,
    setPromotion,
    fetchAssets,
    originalPromotion,
    fetchPromotions,
    setOriginalPromotion,
    setShowSaveWarning,
    selectedImageIndices,
    setSelectedImageIndices,
    onActiveUploadsChange,
}) => {
    // State to manage selected categories (assuming it's used within PromotionTab)
    const [selectedCategories, setSelectedCategories] = useState<
        PromotionType[]
    >(promotion?.types || []);

    // State to manage selected media (assuming it's used within PromotionAssetsTab)
    const [selectedMedia, setSelectedMedia] = useState<Assets[]>([]);

    // Track active uploads from child components
    const [activeUploadsCount, setActiveUploadsCount] = useState(0);
    const [showTabWarning, setShowTabWarning] = useState(false);
    const [pendingTab, setPendingTab] = useState<"categories" | "promotion" | null>(null);

    // Forward upload count changes to parent
    const handleActiveUploadsChange = (count: number) => {
       setActiveUploadsCount(count);
        if (onActiveUploadsChange) {
            onActiveUploadsChange(count);
        }
    };

    // Auto-close tab warning if uploads complete
    useEffect(() => {
        if (showTabWarning && activeUploadsCount === 0) {
            setShowTabWarning(false);
            setPendingTab(null);
        }
    }, [activeUploadsCount, showTabWarning]);

    const selectedAssetCount = assets.filter(
        (asset) =>
            isPromotionType(asset.assetType) &&
            selectedCategories.includes(asset.assetType)
    ).length;

    const [isUploading, setIsUploading] = useState(false);

    const isMobile = useIsMobile(1300);

    const handleSave = async () => {
        if (!promotion || !originalPromotion) {
            toast.error(
                <CustomToast
                    message="Failed to save promotion. Please try again later."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        // Check if there are changes
        const hasChanges =
            JSON.stringify(selectedCategories) !==
                JSON.stringify(originalPromotion.types) ||
            promotion.color !== originalPromotion.color;

        if (!hasChanges) {
            toast.success(
                <CustomToast message="Promotion Saved" type="success" />,
                {
                    autoClose: 3000,
                }
            );
            setIsEditing(false);
            return;
        }

        if (!selectedCategories || selectedCategories.length === 0) {
            toast.error(
                <CustomToast
                    message="Please select at least one category."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
            return;
        }

        setLoading(true);

        try {
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const response = await axios.patch(
                `${backendUrl}/promotions/${promotion.id}`,
                { types: selectedCategories, color: promotion.color },
                { headers }
            );

            if (!response.data) {
                throw new Error("Response data is null.");
            }

            if (response.status === 200) {
                // Assuming response.data contains the updated promotion
                setPromotion(response.data);
                setOriginalPromotion(response.data);
                setIsEditing(false);

                // Check the updated campaignStatus
                if (
                    response.data.campaignStatus?.toString().toLowerCase() ===
                    "active"
                ) {
                    setShowSaveWarning(true);
                } else {
                    toast.success(
                        <CustomToast
                            message="Promotion saved successfully."
                            type="success"
                        />,
                        {
                            autoClose: 3000,
                        }
                    );
                }
            } else {
                throw new Error("Failed to save promotion.");
            }
        } catch (error: unknown) {
            console.error("Failed to save promotion:", error);
            toast.error(
                <CustomToast
                    message="Failed to save promotion. Please try again later."
                    type="error"
                />,
                {
                    autoClose: 3000,
                }
            );
        } finally {
            setLoading(false);
        }
    };

    const editHandler = () => {
        if (!isEditing) {
            setSelectedCategories(promotion?.types || []);
            setIsEditing(true);
        } else {
            handleSave();
            setIsEditing(false);
        }
    };

    // Handle tab changes with upload protection
    const handleTabChange = (value: string) => {
        const newTab = value as "categories" | "promotion";
        
        // Only warn when switching AWAY from the Assets tab (promotion) during active uploads
        // Allow switching TO the Assets tab even with uploads
        if (activeUploadsCount > 0 && activeTab === "promotion" && newTab !== activeTab) {
            setPendingTab(newTab);
            setShowTabWarning(true);
            return;
        }

        console.log('🔄 Tab changed to:', newTab, '- Resetting any pending warnings');
        setActiveTab(newTab);
        setPendingTab(null);
    };

    // Confirm tab change despite uploads
    const confirmTabChange = () => {
        sessionStorage.removeItem('uploadsInterrupted');
        if (pendingTab) {
            // Reset upload count to 0 to stop blocking navigation
            setActiveUploadsCount(0);
            if (onActiveUploadsChange) {
                onActiveUploadsChange(0);
            }
            setActiveTab(pendingTab);
        }
        setShowTabWarning(false);
        setPendingTab(null);
    };

    // Cancel tab change
    const cancelTabChange = () => {
        setShowTabWarning(false);
        setPendingTab(null);
    };

    return (
        <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
        >
            {/* Responsive Container: Stack vertically on mobile, horizontally on desktop */}
            <div
                className={`w-full flex ${
                    isMobile ? "flex-col mb-2" : "flex-row mb-4"
                } justify-between items-start md:items-center`}
            >
                {/* TabsList: Scrollable on mobile */}
                <TabsList
                    isMobile={isMobile}
                    className={`w-full ${
                        isMobile
                            ? "flex overflow-x-auto space-x-2 mb-2 mt-2 md:mb-0"
                            : "space-x-4"
                    }`}
                >
                    <TabsTrigger
                        value="categories"
                        className="whitespace-nowrap px-4 py-2 text-sm md:text-base "
                    >
                        Categories
                    </TabsTrigger>
                    {promotion ? (
                        <TabsTrigger
                            value="promotion"
                            className="whitespace-nowrap px-4 py-2 text-sm md:text-base"
                        >
                            Assets ({selectedAssetCount})
                        </TabsTrigger>
                    ) : null}
                    {isMobile && !isEditing ? (
                        <Button
                            id="edit-save-promo-btn"
                            variant="secondary"
                            onClick={editHandler}
                        >
                            <FaEdit className="mr-2 h-6 w-6 text-white" />
                            {isEditing ? "Save" : "Edit"}
                        </Button>
                    ) : null}
                </TabsList>

                {/* Tab Buttons: Positioned below tabs on mobile */}
                <TabButtons
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    promotion={promotion}
                    setLoading={setLoading}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    setPromotion={setPromotion}
                    selectedMedia={selectedMedia}
                    setSelectedMedia={setSelectedMedia}
                    fetchAssets={fetchAssets}
                    originalPromotion={originalPromotion}
                    fetchPromotions={fetchPromotions}
                    setOriginalPromotion={setOriginalPromotion}
                    isMobile={isMobile}
                    handleSave={handleSave}
                    mediaLength={selectedMedia.length}
                />
            </div>

            {/* Categories Tab Content */}
            <PromotionTab
                isEditing={isEditing}
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                promotion={promotion}
                setPromotion={setPromotion}
            />

            {/* Assets Tab Content */}
            {promotion?.id ? (
                <PromotionAssetsTab
                    isEditing={isEditing}
                    assets={assets}
                    promotionTypes={promotion.types}
                    promotionId={promotion.id}
                    selectedMedia={selectedMedia}
                    setSelectedMedia={setSelectedMedia}
                    carouselApi={carouselApi}
                    carouselAssetIndexMap={carouselAssetIndexMap}
                    fetchAssets={fetchAssets}
                    activePromotionType={activePromotionType}
                    setActivePromotionType={setActivePromotionType}
                    value={activeTab}
                    setLoading={setLoading}
                    isMobile={isMobile}
                    selectedImageIndices={selectedImageIndices}
                    setSelectedImageIndices={setSelectedImageIndices}
                    isUploading={isUploading}
                    setIsUploading={setIsUploading}
                    onActiveUploadsChange={handleActiveUploadsChange}
                />
            ) : null}

            {/* Tab Switch Warning Modal */}
            <UploadNavigationWarning
                isOpen={showTabWarning}
                onClose={cancelTabChange}
                onLeave={confirmTabChange}
                onStay={cancelTabChange}
            />
        </Tabs>
    );
};

export default TabsComponent;
